import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

import { NavbarComponent } from '@shared/components/navbar/navbar.component';
import { SidebarModernComponent } from '@shared/components/sidebar-modern/sidebar-modern.component';
import { FooterComponent } from '@shared/components/footer/footer.component';
import { SpinnerComponent } from '@shared/components/ui/spinner/spinner.component';
import { ToastContainerComponent } from '@shared/components/ui/toast/toast-container.component';
import { ToastService } from '@shared/components/ui/toast/toast.service';
import { ConfirmDialogComponent } from '@shared/components/ui/confirm-dialog/confirm-dialog.component';
import { SessionTimeoutDialogComponent } from '@shared/components/session-timeout-dialog/session-timeout-dialog.component';
import { SkipLinksComponent } from '@shared/components/skip-links/skip-links.component';
import { CommandPaletteComponent } from '@shared/components/command-palette';
import { SymbolHoverCardComponent } from '@shared/components/symbol-hover-card';
import { GlossaryOverlayComponent } from '@shared/components/glossary-overlay';
import { TourOverlayComponent } from '@shared/components/tour-overlay';
import { SymbolSearchComponent } from '@shared/components/symbol-search/symbol-search.component';
import { ReplayBarComponent } from './features/trading/components/replay-bar/replay-bar.component';
import { CopilotChatComponent } from './features/copilot-chat/copilot-chat.component';
import { AgentChatService } from './features/copilot-chat/agent-chat.service';
import { AuthService } from '@core/services/auth.service';
import { HotkeyService } from '@core/services/hotkey.service';
import { CommandPaletteService } from '@core/services/command-palette.service';
import { TourService } from '@core/services/tour.service';
import { ReplayService } from '@core/services/replay.service';
import { LoadingService } from '@core/services/loading.service';
import { ThemeService } from '@core/services/theme.service';
import { MenuService } from '@core/services/menu.service';
import { SessionTimeoutService } from '@core/services/session-timeout.service';
import { CrossTabAuthService } from '@core/services/cross-tab-auth.service';
import { LoggerService } from '@core/services/logger.service';
import { isAnyHttpMockActive, getActiveMockLabels } from '@core/config/mock-flags';
import { AccessibilityService } from '@shared/services/accessibility.service';
import { AppUpdateBannerComponent } from '@shared/components/app-update-banner/app-update-banner.component';
import { PublishHeaderBottomDirective } from '@shared/directives/publish-header-bottom.directive';
import { MarketDataStore } from '@stores/market-data.store';
import { TenantStore } from '@stores/tenant.store';

@Component({
  selector: 'app-root',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterOutlet,
    NavbarComponent,
    SidebarModernComponent,
    FooterComponent,
    SpinnerComponent,
    ToastContainerComponent,
    ConfirmDialogComponent,
    SessionTimeoutDialogComponent,
    SkipLinksComponent,
    CommandPaletteComponent,
    SymbolHoverCardComponent,
    GlossaryOverlayComponent,
    TourOverlayComponent,
    SymbolSearchComponent,
    ReplayBarComponent,
    CopilotChatComponent,
    AppUpdateBannerComponent,
    PublishHeaderBottomDirective
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  private readonly document = inject(DOCUMENT);

  // Core services
  authService = inject(AuthService);
  loadingService = inject(LoadingService);
  themeService = inject(ThemeService);
  menuService = inject(MenuService);
  private readonly marketDataStore = inject(MarketDataStore);
  private readonly tenantStore = inject(TenantStore);
  private router = inject(Router);

  // Session management services (DEV-406)
  readonly sessionTimeoutService = inject(SessionTimeoutService);
  readonly crossTabAuthService = inject(CrossTabAuthService);
  private readonly logger = inject(LoggerService).withContext({ component: 'AppComponent' });
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly accessibilityService = inject(AccessibilityService);
  private readonly hotkeys = inject(HotkeyService);
  private readonly palette = inject(CommandPaletteService);
  /** §18 · ⌘J/Ctrl+J toggles the copilot panel. Registered ONLY while the
   * copilot triple gate (build flag AND runtime /copilot/status AND the
   * PERMISSION-ONLY `copilot:chat:read` check — exact or wildcard-satisfied,
   * never a role-name shortcut; GAP-CP-005-B) is open — a registered binding
   * preventDefaults the browser's own Ctrl/Cmd+J (Downloads), which must keep
   * working for users the copilot is dark for. */
  protected readonly copilotChat = inject(AgentChatService);
  private copilotHotkeyDispose: (() => void) | null = null;
  private readonly copilotHotkeyEffect = effect(() => {
    const enabled = this.copilotChat.enabled();
    this.copilotHotkeyDispose?.();
    this.copilotHotkeyDispose = null;
    if (!enabled) {
      return;
    }
    this.copilotHotkeyDispose = this.hotkeys.register(
      {
        keys: 'Mod+J',
        description: 'Toggle Copilot',
        handler: () => {
          if (this.copilotChat.isOpen()) {
            this.copilotChat.closePanel();
          } else {
            this.copilotChat.openPanel();
          }
        },
        allowInInputs: true,
      },
      { until: this.destroyRef }
    );
  });
  /**
   * RF-QA-173 · `@defer (when …)` trigger for `<app-copilot-chat />`. Reuses
   * the SAME triple gate the panel and its launcher pill already render behind
   * (`build flag AND /copilot/status AND copilot:chat:read`), so the chunk is
   * fetched exactly for the operators who can see the surface and never for
   * anyone else. Widening this predicate widens the download audience.
   */
  readonly copilotEnabled = this.copilotChat.enabled;
  private readonly tour = inject(TourService);
  /** RF-QA-173 · `@defer (when …)` trigger for `<app-tour-overlay>`. */
  readonly tourActive = this.tour.activeTour;
  readonly replayService = inject(ReplayService);

  // GAP-FE-001 · Symbol Search modal open-state. Bound to <app-symbol-search>
  // in the template; toggled by the Mod+Shift+S hotkey below.
  readonly symbolSearchOpen = signal(false);
  // The root store is the canonical tenant-scoped symbol source. Normalize and
  // deduplicate for stable search results without inventing a fallback universe.
  readonly symbolSearchUniverse = computed<readonly string[]>(() =>
    Array.from(
      new Set(
        this.marketDataStore
          .activeSymbols()
          .map(symbol => symbol.symbol.trim().toUpperCase())
          .filter(Boolean)
      )
    ).sort((left, right) => left.localeCompare(right))
  );
  readonly symbolSearchLoading = this.marketDataStore.symbolsLoading;
  readonly symbolSearchError = this.marketDataStore.symbolsError;

  // A request begun under tenant A must not survive a switch/logout. `reset()`
  // invalidates in-flight symbol requests in MarketDataStore; the replacement
  // load is issued only after an authenticated tenant context exists.
  private symbolTenantScope: string | null | undefined;
  private readonly symbolTenantScopeEffect = effect(() => {
    const tenantId = this.authService.isAuthenticated()
      ? this.tenantStore.currentTenant()?.id ?? null
      : null;
    if (tenantId === this.symbolTenantScope) {
      return;
    }

    this.symbolTenantScope = tenantId;
    this.marketDataStore.reset();
    if (tenantId) {
      void this.marketDataStore.loadSymbols();
    }
  });

  // GAP-C18: Visible indicator when mock data is active in non-production environments.
  // Evaluates once at module instantiation — flag list centralized in core/config/mock-flags.ts.
  readonly isMockDataActive = isAnyHttpMockActive();
  readonly activeMockServicesDisplay = getActiveMockLabels().join(', ');

  /**
   * Initialized from document.defaultView.location.pathname to prevent layout flash.
   * Without this, isAuthPage defaults to false, causing the sidebar/navbar
   * to render briefly before the auth guard redirects to /auth/login.
   * That flash triggers 19+ wasted SVG icon HTTP requests.
   *
   * Uses a signal so OnPush change detection properly picks up updates
   * from NavigationEnd events without manual ChangeDetectorRef.markForCheck().
   */
  isAuthPage = signal(
    this.document.defaultView?.location.pathname.startsWith('/auth') ?? false
  );

  constructor() {
    // React to session timeout warning visibility
    effect(() => {
      const isWarningVisible = this.sessionTimeoutService.isWarningVisible();
      if (isWarningVisible) {
        this.logger.debug('[AppComponent] Session timeout warning is visible');
      }
    });
  }

  ngOnInit(): void {
    // Check if current route is auth page
    this.checkAuthPage();

    // GAP-005 · Global ⌘K / Ctrl+K opens the command palette.
    // Bind to root DestroyRef so the disposer fires on app teardown
    // (the underlying document listener was already cleaned up in the
    // service, but keeping the binding tidy too).
    this.hotkeys.register(
      {
        keys: 'Mod+K',
        description: 'Open command palette',
        handler: () => this.palette.toggle(),
        allowInInputs: true,
      },
      { until: this.destroyRef }
    );

    // GAP-FE-001 · ⌘⇧S / Ctrl+Shift+S opens the dedicated Symbol Search
    // modal (FU-10). Previously this fell back to the command palette
    // pre-filtered to symbol entries; now we open the real two-pane modal.
    this.hotkeys.register(
      {
        keys: 'Mod+Shift+S',
        description: 'Symbol search',
        handler: () => this.symbolSearchOpen.set(true),
        allowInInputs: true,
      },
      { until: this.destroyRef }
    );

    // GAP-FE-020 · "?" opens the command palette so operators can
    // discover keyboard shortcuts via fuzzy search. The palette
    // already exposes every registered hotkey description; a dedicated
    // help overlay isn't needed when discovery works via search.
    this.hotkeys.register(
      {
        keys: '?',
        description: 'Open command palette / shortcuts help',
        handler: () => this.palette.open(),
        allowInInputs: false,
      },
      { until: this.destroyRef }
    );

    // GAP-FU-11 · Replay mode is opt-in. The scrubber bar stays hidden until the
    // operator explicitly enters replay via this palette action (⌘K → "replay");
    // the bar's ✕ (exitReplayMode) returns to live. Registered once at init.
    this.palette.registerItems([
      {
        id: 'action:replay-enter',
        label: 'Enter replay mode',
        kind: 'action',
        description: 'Scrub and replay recent buffered market-data events on a timeline',
        keywords: ['replay', 'scrubber', 'playback', 'timeline', 'rewind', 'history', 'time travel'],
        run: () => this.replayService.enterReplayMode(),
      },
    ]);

    // Listen to route changes
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.checkAuthPage();
        // DEV-2035: Clear only non-sticky notifications on route change.
        // Sticky toasts (important notifications) are preserved until explicitly dismissed.
        this.toastService.dismissNonSticky();
        // GAP-015: Announce page navigation to screen readers
        this.announcePageNavigation();
        // GAP-FU-01: Record the visit so the sidebar Recent rail reflects the
        // operator's recent activity (the rail was empty because the palette's
        // trackRouteVisit() had no caller).
        this.trackRecentRoute();
        // The first-run tour is gated on being authenticated and off the auth
        // routes; re-evaluate after each navigation so it starts on the first
        // product route the operator lands on post-login.
        this.maybeStartFirstRunTour();
      });

    // Initialize cross-tab auth synchronization
    this.initializeCrossTabAuth();

    // Auth is automatically initialized in the service constructor

    // GAP-FU-12 · Kick off the first-run tour (no-op on subsequent logins
    // — the tour persists completion to localStorage keyed by tour id).
    // Deferred to a microtask so the navbar / sidebar paint first; the
    // tour body references those surfaces in prose.
    queueMicrotask(() => this.maybeStartFirstRunTour());
  }

  /**
   * Initialize cross-tab authentication synchronization (DEV-406)
   */
  private initializeCrossTabAuth(): void {
    this.crossTabAuthService.initialize();

    // Handle logout events from other tabs
    this.crossTabAuthService.logout$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.logger.info('[AppComponent] Received logout event from another tab');
        // Logout locally without broadcasting (to prevent loop)
        this.authService.logout();
      });

    // Handle token refresh events from other tabs
    this.crossTabAuthService.tokenRefresh$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((_message) => {
        this.logger.debug('[AppComponent] Token refreshed in another tab');
        // Token is already updated via cookie, just log for debugging
      });

    // Handle session expired events from other tabs
    this.crossTabAuthService.sessionExpired$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.logger.warn('[AppComponent] Session expired in another tab');
        this.authService.logout();
      });

    this.logger.info('[AppComponent] Cross-tab auth synchronization initialized');
  }

  /**
   * Handle session extension from timeout dialog
   */
  onExtendSession(): void {
    this.logger.debug('[AppComponent] User requested session extension');
    this.sessionTimeoutService.extendSession();
    this.crossTabAuthService.broadcastSessionExtended();
  }

  /**
   * Handle logout from timeout dialog
   */
  async onLogout(): Promise<void> {
    this.logger.info('[AppComponent] User logged out via timeout dialog');
    await this.crossTabAuthService.broadcastLogout();
    this.sessionTimeoutService.logout();
  }

  private checkAuthPage(): void {
    const url = this.router.url;
    this.isAuthPage.set(url.startsWith('/auth'));
  }

  /**
   * GAP-FU-12 · Start the first-run tour ONLY for an authenticated operator on
   * a product route.
   *
   * Previously this fired unconditionally at app init, so a first-time visitor
   * got the tour's modal backdrop rendered over the LOGIN form — it intercepted
   * pointer events and made the Sign In button unclickable. Caught by the
   * production live smoke on a fresh browser profile (2026-07-27).
   *
   * `startIfFirstRun` is itself idempotent (it no-ops once the tour id is in
   * localStorage), so calling this on every navigation is safe.
   */
  private maybeStartFirstRunTour(): void {
    if (!this.authService.isAuthenticated()) return;
    if (this.router.url.startsWith('/auth')) return;
    this.tour.startIfFirstRun('operator');
  }

  /** GAP-FE-001 · Symbol picked from the dedicated Symbol Search modal. */
  onSymbolSelected(symbol: string): void {
    this.symbolSearchOpen.set(false);
    void this.router.navigate(['/trading/order'], { queryParams: { symbol } });
  }

  retrySymbolSearch(): void {
    if (this.authService.isAuthenticated() && this.tenantStore.currentTenant()) {
      void this.marketDataStore.loadSymbols();
    }
  }

  /**
   * GAP-015 / GAP-FU-01 · Derive route segments + a human-readable page label
   * from the current URL. Shared by the SR announcement and the Recent rail so
   * both stay consistent. Walks backwards past trailing ID segments (UUID /
   * numeric) to the last meaningful segment for the label.
   */
  private describeCurrentRoute(): { segments: string[]; label: string; hasId: boolean } {
    const url = this.router.url.split('?')[0].split('#')[0];
    const segments = url.split('/').filter(Boolean);

    const idPattern = /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$|^\d+$/i;
    const hasId = segments.some((s: string) => idPattern.test(s));
    const meaningful = [...segments].reverse().find((s: string) => !idPattern.test(s));
    const segment = meaningful ?? 'home';

    const label = segment
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
    return { segments, label, hasId };
  }

  /** GAP-015: Derive a human-readable page name from the URL and announce it to screen readers. */
  private announcePageNavigation(): void {
    this.accessibilityService.announceNavigation(this.describeCurrentRoute().label);
  }

  /**
   * GAP-FU-01: Record the visited route so it surfaces in the sidebar Recent
   * rail (last 5 palette entries). Auth pages and the bare root are skipped —
   * there is nothing meaningful to jump back to. `trackRouteVisit` dedups by
   * `route:<segments>` id, so revisiting a page re-timestamps one entry.
   */
  private trackRecentRoute(): void {
    if (this.isAuthPage()) {
      return;
    }
    const { segments, label, hasId } = this.describeCurrentRoute();
    // Skip empty (bare root) and detail/param routes. A param route carries a
    // UUID/numeric id, so every distinct record would be a distinct entry that
    // nonetheless collapses to the SAME label ("Strategies"), flooding the
    // 5-slot rail with duplicate-looking rows and evicting useful section
    // entries. Only clean section/list routes are recorded (they dedup by id).
    if (segments.length === 0 || hasId) {
      return;
    }
    this.palette.trackRouteVisit(segments, label);
  }
}
